import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const ProfitCalculator = ({ projectData, costs, onDataChange }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value || 0);
  };

  const profitScenarios = [
    { margin: 25, label: 'Conservador', icon: 'Shield', color: 'text-blue-600' },
    { margin: 50, label: 'Equilibrado', icon: 'Target', color: 'text-green-600' },
    { margin: 75, label: 'Agressivo', icon: 'TrendingUp', color: 'text-orange-600' },
    { margin: 100, label: 'Premium', icon: 'Crown', color: 'text-purple-600' }
  ];

  const competitivePrices = [
    { service: 'Concorrente A', price: costs?.finalPrice * 1.15, quality: 'Standard' },
    { service: 'Concorrente B', price: costs?.finalPrice * 0.95, quality: 'Economy' },
    { service: 'Mercado Premium', price: costs?.finalPrice * 1.35, quality: 'Premium' }
  ];

  return (
    <div className="bg-card border border-border rounded-xl p-8">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-success/10 rounded-xl flex items-center justify-center">
          <Icon name="TrendingUp" size={20} className="text-success" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-foreground">Calculadora de Lucro</h3>
          <p className="text-sm text-muted-foreground">Defina sua margem e analise a competitividade</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profit Configuration */}
        <div className="space-y-6">
          <div>
            <h4 className="font-medium text-foreground mb-4 flex items-center space-x-2">
              <Icon name="Settings" size={16} />
              <span>Configuração de Margem</span>
            </h4>
            
            {/* Current Margin Display */}
            <div className="mb-4 p-4 bg-primary/5 rounded-xl border border-primary/20">
              <div className="flex justify-between items-center mb-2">
                <span className="text-muted-foreground">Margem Atual:</span>
                <span className="text-2xl font-bold text-primary">
                  {projectData?.clientMarkup}%
                </span>
              </div>
              <div className="text-sm text-muted-foreground">
                Lucro: {formatCurrency(costs?.profit)} • 
                Preço Final: {formatCurrency(costs?.finalPrice)}
              </div>
            </div>

            {/* Margin Slider */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-foreground">
                Ajustar Margem de Lucro
              </label>
              <input
                type="range"
                min="10"
                max="200"
                step="5"
                value={projectData?.clientMarkup}
                onChange={(e) => onDataChange('clientMarkup', parseInt(e?.target?.value))}
                className="w-full h-2 bg-muted rounded-xl appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>10%</span>
                <span>50%</span>
                <span>100%</span>
                <span>200%</span>
              </div>
            </div>
          </div>

          {/* Quick Margin Scenarios */}
          <div>
            <h4 className="font-medium text-foreground mb-3">Cenários Rápidos</h4>
            <div className="grid grid-cols-2 gap-2">
              {profitScenarios?.map((scenario) => {
                const isSelected = projectData?.clientMarkup === scenario?.margin;
                return (
                  <button
                    key={scenario?.margin}
                    onClick={() => onDataChange('clientMarkup', scenario?.margin)}
                    className={`
                      p-3 rounded-xl border text-left transition-all duration-200
                      ${isSelected 
                        ? 'border-primary bg-primary/10 text-primary' :'border-border bg-card hover:border-primary/50'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <Icon name={scenario?.icon} size={16} className={scenario?.color} />
                      <span className="font-medium text-sm">{scenario?.label}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {scenario?.margin}% • {formatCurrency(costs?.total * (scenario?.margin / 100))}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Pricing Channels Controls */}
          <div className="space-y-4">
            <h4 className="font-medium text-foreground mb-2 flex items-center space-x-2">
              <Icon name="Store" size={16} />
              <span>Parâmetros de Preço (Atacado/Varejo)</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-muted/20 border border-border">
                <h5 className="font-medium text-sm mb-3 flex items-center space-x-2">
            <Icon name="Filament" size={14} />
                  <span>Atacado</span>
                </h5>
                <Input
                  label="Markup (%)"
                  type="number" min="0" max="500" step="1"
                  value={projectData?.wholesaleMarkup ?? 150}
                  onChange={(e) => onDataChange('wholesaleMarkup', parseFloat(e?.target?.value))}
                />
                <Input
                  label="Desconto (%)"
                  type="number" min="0" max="100" step="1"
                  value={projectData?.wholesaleDiscount ?? 5}
                  onChange={(e) => onDataChange('wholesaleDiscount', parseFloat(e?.target?.value))}
                  className="mt-3"
                />
                <Input
                  label="Impostos (%)"
                  type="number" min="0" max="50" step="0.1"
                  value={projectData?.wholesaleVatPercentage ?? projectData?.vatPercentage ?? 0}
                  onChange={(e) => onDataChange('wholesaleVatPercentage', parseFloat(e?.target?.value))}
                  className="mt-3"
                />
              </div>
              <div className="p-4 rounded-xl bg-muted/20 border border-border">
                <h5 className="font-medium text-sm mb-3 flex items-center space-x-2">
                  <Icon name="ShoppingBag" size={14} />
                  <span>Varejo</span>
                </h5>
                <Input
                  label="Markup (%)"
                  type="number" min="0" max="500" step="1"
                  value={projectData?.retailMarkup ?? 250}
                  onChange={(e) => onDataChange('retailMarkup', parseFloat(e?.target?.value))}
                />
                <Input
                  label="Desconto (%)"
                  type="number" min="0" max="100" step="1"
                  value={projectData?.retailDiscount ?? 10}
                  onChange={(e) => onDataChange('retailDiscount', parseFloat(e?.target?.value))}
                  className="mt-3"
                />
                <Input
                  label="Impostos (%)"
                  type="number" min="0" max="50" step="0.1"
                  value={projectData?.retailVatPercentage ?? 7}
                  onChange={(e) => onDataChange('retailVatPercentage', parseFloat(e?.target?.value))}
                  className="mt-3"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <Input
                label="Quantidade do Cenário"
                type="number" min="1" step="1"
                value={projectData?.scenarioQuantity ?? 100}
                onChange={(e) => onDataChange('scenarioQuantity', parseInt(e?.target?.value))}
              />
              <Input
                label="Falhas (%)"
                type="number" min="0" max="100" step="0.1"
                value={projectData?.failureRate ?? 0}
                onChange={(e) => onDataChange('failureRate', parseFloat(e?.target?.value))}
              />
              <Input
                label="Manutenção (% de depreciação)"
                type="number" min="0" max="100" step="0.1"
                value={projectData?.repairCost ?? 5}
                onChange={(e) => onDataChange('repairCost', parseFloat(e?.target?.value))}
              />
            </div>
          </div>
        </div>

        {/* Results and Analysis */}
        <div className="space-y-6">
          <div>
            <h4 className="font-medium text-foreground mb-4 flex items-center space-x-2">
              <Icon name="BarChart3" size={16} />
              <span>Análise Financeira</span>
            </h4>
            
            {/* Financial Summary */}
            <div className="space-y-4 p-4 bg-muted/20 rounded-xl">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Custo Base (subtotal):</span>
                  <span className="font-semibold text-foreground">{formatCurrency(costs?.subtotal)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Impostos (subtotal):</span>
                  <span className="font-semibold text-foreground">{formatCurrency(costs?.vatAmount)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Total (após impostos):</span>
                  <span className="font-semibold text-foreground">{formatCurrency(costs?.total)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Custo por unidade:</span>
                  <span className="font-semibold text-foreground">{formatCurrency(costs?.pricing?.unitBaseCost)}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 rounded-xl bg-card border border-border">
                  <h5 className="font-medium text-sm mb-2">Custos de Produção</h5>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Material</span><span>{formatCurrency((costs?.totalMaterial || 0) / (projectData?.quantity || 1))}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Energia</span><span>{formatCurrency((costs?.totalElectricity || 0) / (projectData?.quantity || 1))}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Depreciação</span><span>{formatCurrency((costs?.totalDepreciation || 0) / (projectData?.quantity || 1))}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Acabamento</span><span>{formatCurrency((costs?.postProcessCost || 0) / (projectData?.quantity || 1))}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Manutenção</span><span>{formatCurrency((costs?.maintenanceCost || 0) / (projectData?.quantity || 1))}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Falhas</span><span>{formatCurrency((costs?.failureCost || 0) / (projectData?.quantity || 1))}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Retorno do Investimento</span><span>{formatCurrency((costs?.roiCost || 0) / (projectData?.quantity || 1))}</span></div>
                    <div className="flex justify-between font-medium border-t pt-2"><span>Total</span><span>{formatCurrency(((costs?.totalMaterial || 0) + (costs?.totalElectricity || 0) + (costs?.totalDepreciation || 0) + (costs?.postProcessCost || 0) + (costs?.maintenanceCost || 0) + (costs?.failureCost || 0) + (costs?.roiCost || 0)) / (projectData?.quantity || 1))}</span></div>
                  </div>
                </div>
              <div className="p-3 rounded-xl bg-card border border-border">
                  <h5 className="font-medium text-sm mb-2">Custos Administrativos</h5>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Outros Custos</span><span>{formatCurrency((costs?.otherCosts || 0) / (projectData?.quantity || 1))}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Embalagens</span><span>{formatCurrency((costs?.packagingCost || 0) / (projectData?.quantity || 1))}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Marketplace (unitário)</span><span>{formatCurrency(costs?.pricing?.marketplace?.unitTotalFees || 0)}</span></div>
                    <div className="flex justify-between font-medium border-t pt-2"><span>Total Admin</span><span>{formatCurrency(((costs?.otherCosts || 0) / (projectData?.quantity || 1)) + ((costs?.packagingCost || 0) / (projectData?.quantity || 1)) + (costs?.pricing?.marketplace?.unitTotalFees || 0))}</span></div>
                  </div>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-card border border-border">
                <h5 className="font-medium text-sm mb-3">Preço Unitário — Sugerido vs Final</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Atacado (sugerido)</span><span className="font-medium">{formatCurrency(costs?.pricing?.wholesale?.suggestedUnitPrice)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Atacado (final)</span><span className="font-semibold text-primary">{formatCurrency(costs?.pricing?.wholesale?.finalUnitPrice)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Impostos</span><span>{formatCurrency(costs?.pricing?.wholesale?.taxesUnit)}</span></div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Varejo (sugerido)</span><span className="font-medium">{formatCurrency(costs?.pricing?.retail?.suggestedUnitPrice)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Varejo (final)</span><span className="font-semibold text-primary">{formatCurrency(costs?.pricing?.retail?.finalUnitPrice)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Impostos</span><span>{formatCurrency(costs?.pricing?.retail?.taxesUnit)}</span></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-sm">
                  <div className="space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Vendas (Atacado)</span><span>{formatCurrency(costs?.pricing?.wholesale?.salesValue)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Custos</span><span>{formatCurrency(costs?.pricing?.wholesale?.costsTotal)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Lucro líquido</span><span className="font-medium">{formatCurrency(costs?.pricing?.wholesale?.netProfitTotal)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Lucro líquido %</span><span>{(costs?.pricing?.wholesale?.netProfitPercent || 0)?.toFixed(1)}%</span></div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Vendas (Varejo)</span><span>{formatCurrency(costs?.pricing?.retail?.salesValue)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Custos</span><span>{formatCurrency(costs?.pricing?.retail?.costsTotal)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Lucro líquido</span><span className="font-medium">{formatCurrency(costs?.pricing?.retail?.netProfitTotal)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Lucro líquido %</span><span>{(costs?.pricing?.retail?.netProfitPercent || 0)?.toFixed(1)}%</span></div>
                  </div>
                </div>
                <div className="mt-3 p-3 rounded-xl bg-primary/5">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="ArrowRightCircle" size={16} className="text-primary" />
                      <span className="text-sm">Seu preço unitário (varejo):</span>
                    </div>
                    <span className="text-lg font-bold text-primary">{formatCurrency(costs?.pricing?.retail?.finalUnitPrice)}</span>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mt-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="BadgeDollarSign" size={16} className="text-success" />
                      <span className="text-sm">Seu lucro por unidade (varejo):</span>
                    </div>
                    <span className="text-lg font-semibold text-success">{formatCurrency(((costs?.pricing?.retail?.netProfitTotal || 0) / (costs?.pricing?.retail?.quantity || 1)))}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Market Comparison */}
          <div>
            <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
              <Icon name="Users" size={16} />
              <span>Comparação de Mercado</span>
            </h4>
            
            <div className="space-y-2">
              {competitivePrices?.map((competitor, index) => {
                const difference = ((competitor?.price - costs?.finalPrice) / costs?.finalPrice) * 100;
                const isCompetitive = Math.abs(difference) <= 20;
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`
                      flex justify-between items-center p-3 rounded-xl border
                      ${isCompetitive ? 'bg-success/5 border-success/20' : 'bg-muted/20 border-border'}
                    `}
                  >
                    <div>
                      <div className="font-medium text-foreground text-sm">
                        {competitor?.service}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {competitor?.quality}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-foreground">
                        {formatCurrency(competitor?.price)}
                      </div>
                      <div className={`text-xs ${difference > 0 ? 'text-success' : 'text-destructive'}`}>
                        {difference > 0 ? '+' : ''}{difference?.toFixed(1)}%
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            
            <div className="mt-3 p-3 bg-primary/5 rounded-xl">
              <div className="flex items-center space-x-2 text-sm text-primary">
                <Icon name="Info" size={14} />
                <span>
                  Seu preço está {costs?.finalPrice > competitivePrices?.[1]?.price ? 'acima' : 'abaixo'} da média do mercado
                </span>
              </div>
            </div>
          </div>

          {/* Removed ROI Metrics to avoid redundancy with input parameters */}
        </div>
      </div>
    </div>
  );
};

export default ProfitCalculator;
